import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useLocation, useNavigate } from "react-router-dom";

import "./mobileNavigation.css";
import MenuIcon from "@mui/icons-material/Menu";
import {
  ArrowForwardIos,
  AutoAwesomeOutlined,
  Close,
  DashboardOutlined,
  ExpandMore,
  Language,
  LockOutlined,
  LogoutOutlined,
  MenuBookOutlined,
  NotificationsNoneOutlined,
  PersonOutline,
  Search,
  SettingsOutlined,
  StorefrontOutlined,
  BusinessOutlined,
  AssessmentOutlined,
  KeyboardArrowRight,
  SmartphoneRounded,
} from "@mui/icons-material";

import { useTranslation } from "react-i18next";

import { useDispatch } from "react-redux";

import * as Action from "../../../../store/redux/client_reducer.js";
import * as ActionTerminal from "../../../../store/redux/terminal_reducer.js";

import Logo from "../logo/universe.webp";
import usePublicNavigation from "./usePublicNavigation";
import { CLIENT_SIGN_IN, STAFF_SIGN_IN } from "./publicNavigationState";
import { staffLogOut } from "../../../../store/redux/universeStaffReducer.js";
import { configureStaffSocket } from "../../../staff_directory/universe-fdc-sprint1/services/fdcSocket";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";

import { homeMobileMenus } from "./mobileHomepageMenuData";

export default function MobileNavigation({ setLoading }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { i18n } = useTranslation();
  const { account, unread, theme, updateTheme } = usePublicNavigation();
  const isAuthenticated = account.type !== "visitor";

  /*
  |--------------------------------------------------------------------------
  | STATE
  |--------------------------------------------------------------------------
  */

  const [expandedMenu, setExpandedMenu] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  /*
  |--------------------------------------------------------------------------
  | REDUX
  |--------------------------------------------------------------------------
  */

  // Both public headers share the same Redux-derived, role-aware account data.
  const fullName = account.name;
  const profileImage = account.image;

  /*
  |--------------------------------------------------------------------------
  | CLOSE / ESCAPE / BODY SCROLL
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    const closeOnDesktop = () => {
      if (window.innerWidth > 768) setOpen(false);
    };
    window.addEventListener("resize", closeOnDesktop);
    return () => window.removeEventListener("resize", closeOnDesktop);
  }, []);

  /*
  |--------------------------------------------------------------------------
  | RESET MENU WHEN NAVIGATION CLOSES
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!open) {
      setExpandedMenu(null);
      setSearchValue("");
    }
  }, [open]);

  /*
  |--------------------------------------------------------------------------
  | TOGGLE DROPDOWN
  |--------------------------------------------------------------------------
  */

  const toggleMenu = (menu) => {
    setExpandedMenu((current) => (current === menu ? null : menu));
  };

  /*
  |--------------------------------------------------------------------------
  | NAVIGATION
  |--------------------------------------------------------------------------
  */

  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);

  const goTo = (path) => {
    setOpen(false);
    setExpandedMenu(null);
    navigate(path);
  };

  /*
  |--------------------------------------------------------------------------
  | ACTIVE ROUTE
  |--------------------------------------------------------------------------
  */

  const isActive = (path) => {
    if (!path) return false;

    return location.pathname === path;
  };

  /*
  |--------------------------------------------------------------------------
  | LOGOUT
  |--------------------------------------------------------------------------
  */

  const logOut = () => {
    setLoading?.(true);
    if (account.type === "staff") {
      // Same token/socket clean-up as the staff workspace logout.
      configureStaffSocket(null);
      dispatch(staffLogOut());
    } else if (account.type === "client") {
      dispatch(Action.logOut());
      dispatch(ActionTerminal.clearCartAction());
    }
    setOpen(false);
    setExpandedMenu(null);
    navigate("/");
    setLoading?.(false);
  };

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
  };

  /*
  |--------------------------------------------------------------------------
  | PUBLIC NAVIGATION
  |--------------------------------------------------------------------------
  */

  const publicItems = [
    { id: "features", title: "Features", icon: <AutoAwesomeOutlined />, groups: homeMobileMenus.features },
    { id: "solutions", title: "Solutions", icon: <BusinessOutlined />, groups: homeMobileMenus.solutions },
    { id: "enterprise", title: "Enterprise", icon: <StorefrontOutlined />, path: "/enterprise" },
    { id: "learn", title: "Learn", icon: <MenuBookOutlined />, groups: homeMobileMenus.learn },
    { id: "pricing", title: "Pricing", icon: <AssessmentOutlined />, path: "/pricing" },
    { id: "company", title: "Company", icon: <BusinessOutlined />, groups: homeMobileMenus.company },
    { id: "mobile-apps", title: "Mobile Apps", icon: <SmartphoneRounded />, path: "/store/universe-mobile/application" },
  ];

  /*
  |--------------------------------------------------------------------------
  | AUTHENTICATED TOOLS
  |--------------------------------------------------------------------------
  */

  // Only existing, verified destinations are shown. No non-functional menu buttons.
  const authenticatedTools = [
    { title: "Open workspace", icon: <DashboardOutlined />, path: account.workspace },
    ...(account.profile ? [{ title: "My profile", icon: <PersonOutline />, path: account.profile }] : []),
    ...(account.notifications ? [{ title: "Notifications", icon: <NotificationsNoneOutlined />, path: account.notifications }] : []),
    ...(account.settings ? [{ title: "Settings", icon: <SettingsOutlined />, path: account.settings }] : []),
    ...(account.type === "client" ? [{ title: "Point of Sales", icon: <StorefrontOutlined />, path: "/clients/point-of-sale/workspace/inventory-transactions" }] : []),
  ];

  /*
  |--------------------------------------------------------------------------
  | SEARCH
  |--------------------------------------------------------------------------
  */

  const query = searchValue.trim().toLowerCase();
  const filteredPublicItems = publicItems
    .map((item) => {
      if (!item.groups || !query || item.title.toLowerCase().includes(query)) return item;
      const groups = item.groups
        .map((group) => ({
          ...group,
          items: group.items.filter((child) =>
            `${child.title} ${child.description || ""}`.toLowerCase().includes(query)
          ),
        }))
        .filter((group) => group.items.length > 0);
      return { ...item, groups };
    })
    .filter((item) =>
      !query || item.title.toLowerCase().includes(query) || (item.groups?.length > 0)
    );

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <>
      <div className="mobileNavWrap">
      {!open && (
        <button
          type="button"
          className="mobileHeaderMenuButton"
          onClick={() => setOpen(true)}
          aria-label="Open navigation"
        >
          <MenuIcon fontSize="large" />
        </button>
      )}

      </div>
      {typeof document !== "undefined" && createPortal(
        <>
      {/* =====================================================
          BACKDROP
      ===================================================== */}

      <div
        className={`mobileNavBackdrop ${
          open ? "mobileNavBackdrop--visible" : ""
        }`}
        onClick={() => setOpen(false)}
      />

      {/* =====================================================
          NAVIGATION
      ===================================================== */}

      <aside
        className={`mobileNavigation ${open ? "mobileNavigation--open" : ""}`}
        data-nav-theme={theme}
        aria-label="Mobile navigation"
        aria-hidden={!open}
      >
        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="mobileNavigationHeader">
          <button
            type="button"
            className="mobileNavigationLogo fx-ac"
            onClick={() => goTo("/")}
            aria-label="Go to Universe Inventory home"
          >
            <img src={Logo} alt="Universe Inventory" />
          </button>

          <button
            type="button"
            className="mobileNavigationClose fx-ac fx-jc"
            onClick={() => setOpen(false)}
            aria-label="Close navigation"
          >
            <Close />
          </button>
        </div>

        {/* =====================================================
            ACCOUNT HEADER
        ===================================================== */}

        {isAuthenticated && (
          <div className="mobileAccountCard">
            <div className="mobileAccountIdentity fx-ac">
              <div className="mobileAccountAvatar fx-ac fx-jc">
                {profileImage ? (
                  <img src={profileImage} alt={fullName} />
                ) : (
                  <PersonOutline />
                )}
              </div>

              <div className="mobileAccountInfo fx-cl">
                <span className="mobileAccountName">{fullName}</span>

                <span className="mobileAccountRole">{account.type === "staff" ? `Universe ${account.role}` : "Client account"}</span>
              </div>
            </div>

            <button
              type="button"
              className="mobileWorkspaceButton fx-ac fx-jb"
              onClick={() => goTo(account.workspace)}
            >
              <span>Launch Inventory Workspace</span>

              <ArrowForwardIos />
            </button>
          </div>
        )}

        {/* =====================================================
            SEARCH
        ===================================================== */}

        <div className="mobileNavigationSearch">
          <Search />

          <input
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search navigation..."
            aria-label="Search navigation"
          />

          {searchValue && (
            <button
              type="button"
              onClick={() => setSearchValue("")}
              aria-label="Clear navigation search"
            >
              <Close />
            </button>
          )}
        </div>

        {/* =====================================================
            QUICK ACTIONS
        ===================================================== */}

        {isAuthenticated && account.notifications && (
          <div className="mobileQuickActions">
            <button type="button" onClick={() => goTo(account.notifications)}>
              <NotificationsNoneOutlined />
              <span>Notifications</span>
              {Number.isInteger(unread) && unread > 0 && <strong className="mobilePublicUnreadBadge">{unread > 99 ? "99+" : unread}</strong>}
            </button>
          </div>
        )}

        {/* =====================================================
            MAIN NAVIGATION
        ===================================================== */}

        <div className="mobileNavigationScroll">
          <div className="mobileNavigationSection">
            <span className="mobileNavigationSectionLabel">
              {isAuthenticated ? "Explore" : "Universe Inventory"}
            </span>

            <nav
              className="mobileNavigationList"
              aria-label="Primary navigation"
            >
              {filteredPublicItems.length === 0 && (
                <p className="mobileNavigationNoResults">No matching links. Try another search.</p>
              )}
              {filteredPublicItems.map((item) => {
                const hasChildren = Boolean(item.groups);

                /*
                |--------------------------------------------------------------------------
                | NORMAL LINK
                |--------------------------------------------------------------------------
                */

                if (!hasChildren) {
                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`mobileNavigationItem ${
                        isActive(item.path)
                          ? "mobileNavigationItem--active"
                          : ""
                      }`}
                      onClick={() => goTo(item.path)}
                    >
                      <span className="mobileNavigationItemIcon">
                        {item.icon}
                      </span>

                      <span className="mobileNavigationItemText">
                        {item.title}
                      </span>

                      <KeyboardArrowRight />
                    </button>
                  );
                }

                /*
                |--------------------------------------------------------------------------
                | DROPDOWN ITEM
                |--------------------------------------------------------------------------
                */

                const isExpanded = expandedMenu === item.id || Boolean(query);

                return (
                  <div
                    className={`mobileNavigationGroup ${
                      isExpanded ? "mobileNavigationGroup--open" : ""
                    }`}
                    key={item.id}
                  >
                    <button
                      type="button"
                      className="mobileNavigationItem"
                      onClick={() => toggleMenu(item.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`home-mobile-submenu-${item.id}`}
                    >
                      <span className="mobileNavigationItemIcon">
                        {item.icon}
                      </span>

                      <span className="mobileNavigationItemText">
                        {item.title}
                      </span>

                      <ExpandMore
                        className={
                          isExpanded ? "mobileNavigationExpandIcon--open" : ""
                        }
                      />
                    </button>

                    {isExpanded && (
                      <div className="mobileNavigationNested mobileNavigationNested--links" id={`home-mobile-submenu-${item.id}`}>
                        {item.groups.map((group) => (
                          <div className="mobileNavigationLinkGroup" key={group.title}>
                            <div className="mobileNavigationLinkGroupTitle">{group.title}</div>
                            {group.items.map((child) => (
                              <button
                                type="button"
                                className={`mobileNavigationSubLink ${isActive(child.path) ? "mobileNavigationSubLink--active" : ""}`}
                                key={child.path}
                                onClick={() => goTo(child.path)}
                              >
                                <span className="mobileNavigationSubLinkDot" />
                                <span className="mobileNavigationSubLinkContent"><strong>{child.title}</strong><small>{child.description}</small></span>
                                <KeyboardArrowRight />
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          {/* =====================================================
              AUTHENTICATED BUSINESS AREA
          ===================================================== */}

          {isAuthenticated && (
            <div className="mobileNavigationSection">
              <span className="mobileNavigationSectionLabel">
                Your workspace
              </span>

              <div className="mobileNavigationList">
                {authenticatedTools.map((item, index) => (
                  <button
                    type="button"
                    key={`${item.title}-${index}`}
                    className="mobileNavigationItem"
                    onClick={() => goTo(item.path)}
                  >
                    <span className="mobileNavigationItemIcon">
                      {item.icon}
                    </span>

                    <span className="mobileNavigationItemText">
                      {item.title}
                    </span>

                    <KeyboardArrowRight />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* =====================================================
              ACCOUNT ACTIONS
          ===================================================== */}

          <div className="mobileNavigationSection">
            <span className="mobileNavigationSectionLabel">Account</span>

            <div className="mobileNavigationList">
              {/* Explicit language selection: never switch languages unexpectedly. */}
              <div className="mobilePublicPreferences">
                <div className="mobilePublicPrefLabel"><Language fontSize="small" /> Website language</div>
                <div className="mobilePublicChoiceGroup" role="group" aria-label="Website language">
                  <button type="button" aria-pressed={i18n.language?.startsWith("en")} onClick={() => changeLanguage("en")}>English</button>
                  <button type="button" aria-pressed={i18n.language?.startsWith("fr")} onClick={() => changeLanguage("fr")}>Français</button>
                </div>
                <div className="mobilePublicPrefLabel"><LightModeOutlinedIcon fontSize="small" /> Navigation theme</div>
                <div className="mobilePublicChoiceGroup" role="group" aria-label="Navigation color theme">
                  <button type="button" aria-pressed={theme === "light"} onClick={() => updateTheme("light")}><LightModeOutlinedIcon fontSize="small"/> Light</button>
                  <button type="button" aria-pressed={theme === "dark"} onClick={() => updateTheme("dark")}><DarkModeOutlinedIcon fontSize="small"/> Dark</button>
                </div>
              </div>

              {/* =================================================
                  SIGN IN / SIGN UP
              ================================================= */}

              {!isAuthenticated && (
                <>
                  <button
                    type="button"
                    className="mobileNavigationItem mobileNavigationItem--signIn"
                    onClick={() =>
                      goTo(CLIENT_SIGN_IN)
                    }
                  >
                    <span className="mobileNavigationItemIcon">
                      <LockOutlined />
                    </span>

                    <span className="mobileNavigationItemText">Sign in</span>

                    <KeyboardArrowRight />
                  </button>

                  <button type="button" className="mobileNavigationItem" onClick={() => goTo(STAFF_SIGN_IN)}>
                    <span className="mobileNavigationItemIcon"><BusinessOutlined /></span>
                    <span className="mobileNavigationItemText">Staff sign in</span>
                    <KeyboardArrowRight />
                  </button>

                  <button
                    type="button"
                    className="mobileNavigationItem mobileNavigationItem--signUp"
                    onClick={() => goTo("/create_new_account")}
                  >
                    <span className="mobileNavigationItemIcon">
                      <PersonOutline />
                    </span>

                    <span className="mobileNavigationItemText">
                      Create an account
                    </span>

                    <KeyboardArrowRight />
                  </button>
                </>
              )}

              {/* =================================================
                  LOGOUT
              ================================================= */}

              {isAuthenticated && (
                <button
                  type="button"
                  className="mobileNavigationItem mobileNavigationItem--logout"
                  onClick={logOut}
                >
                  <span className="mobileNavigationItemIcon">
                    <LogoutOutlined />
                  </span>

                  <span className="mobileNavigationItemText">Log out</span>

                  <KeyboardArrowRight />
                </button>
              )}
            </div>
          </div>

          {/* =====================================================
              FOOTER
          ===================================================== */}

          <div className="mobileNavigationFooter">
            <div className="mobileNavigationFooterIcon fx-ac fx-jc">
              <AutoAwesomeOutlined />
            </div>

            <div className="fx-cl">
              <strong>Universe Inventory</strong>

              <span>Manage your business from anywhere.</span>
            </div>
          </div>
        </div>
      </aside>
        </>,
        document.body
      )}
    </>
  );
}
